/**
 * ============================================================================
 * VenueCourtsNew - PRODUCTION-READY COURTS MANAGEMENT
 * ============================================================================
 *
 * Production changes:
 * - Delete removed: vendors can only deactivate courts, not delete them
 * - Bulk actions removed: not needed for typical 2-10 court venues
 * - Clean composition with extracted hooks and components
 *
 * ============================================================================
 */

import React, { useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useVenueRole } from '../hooks/useVenueRole';
import { AlertCircle, RefreshCw } from 'lucide-react';

// Hooks
import { useCourtsData } from '../hooks/useCourtsData';
import { useCourtMutations, DeactivationBlockedError } from '../hooks/useCourtMutations';
import { useCourtSchedules } from '../hooks/useCourtSchedules';

// Components
import { CourtsHeader } from '../components/courts/CourtsHeader';
import { CourtsStats } from '../components/courts/CourtsStats';
import { CourtsTable } from '../components/courts/CourtsTable';
import { CourtFormModal } from '../components/courts/CourtFormModal';
import { DeactivationBlockedModal, DeactivationBlockedData } from '../components/courts/DeactivationBlockedModal';
import TimeSlotCalendar from '../components/TimeSlotCalendar';
import GroupedTimeSlotCalendar from '../components/GroupedTimeSlotCalendar';
import Toast from '../components/Toast';

// Utils
import { getNextCourtNumber } from '../utils/courtFormatters';

// Types
import type { Court, CourtFormData } from '../types/courts';

/**
 * Courts management page - production-ready
 */
export default function VenueCourtsNew() {
  const { user } = useAuth();
  const { can } = useVenueRole();
  const venue = user?.venues?.[0];
  // Court management (add/edit/toggle) requires venue.edit; staff with only
  // schedule.view see the courts read-only.
  const canManageCourts = can('venue.edit');

  // ============================================================================
  // STATE
  // ============================================================================

  const [showCourtForm, setShowCourtForm] = useState(false);
  const [editingCourt, setEditingCourt] = useState<Court | null>(null);
  const [filterSport, setFilterSport] = useState<number | 'all'>('all');

  // Toast notification state
  const [toast, setToast] = useState<{message: string; type: 'success' | 'error' | 'info'} | null>(null);

  // Deactivation blocked modal state
  const [deactivationBlockedData, setDeactivationBlockedData] = useState<DeactivationBlockedData | null>(null);
  const [togglingCourtId, setTogglingCourtId] = useState<number | null>(null);

  // Helper for showing toast
  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToast({ message, type });
  };

  // ============================================================================
  // DATA FETCHING (TanStack Query)
  // ============================================================================

  const { data: courtsData, isPending, error, refetch } = useCourtsData(venue?.id);
  const { addCourt, updateCourt, toggleActive } = useCourtMutations(venue?.id);

  // ============================================================================
  // COMPUTED DATA
  // ============================================================================

  const courts = courtsData?.courts || [];
  const sports = venue?.sports || [];

  // Get today's operating hours for smart polling
  const todayOperatingHours = useMemo(() => {
    if (!venue?.operating_hours) return { is_open: true };

    const today = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const todaySchedule = venue.operating_hours.find(
      (schedule: any) => schedule.day_of_week.toLowerCase() === today
    );

    return {
      is_open: !todaySchedule?.is_closed,
      opening_time: todaySchedule?.opening_time,
      closing_time: todaySchedule?.closing_time,
    };
  }, [venue]);

  // Fetch schedule data for all courts with smart polling
  const { data: scheduleData, isLoading: scheduleLoading } = useCourtSchedules(
    venue?.id,
    courts,
    todayOperatingHours
  );

  const stats = useMemo(() => {
    return {
      totalCourts: courts.length,
      activeCourts: courts.filter((c) => c.is_active).length,
      inactiveCourts: courts.filter((c) => !c.is_active).length,
      courtsWithCustomPricing: courts.filter((c) => c.pricing_source === 'court_custom').length,
    };
  }, [courts]);

  // Group courts by parent-child relationships for Today's Schedule display
  const courtGroups = useMemo(() => {
    const parentMap = new Map<number, { parent: Court; children: Court[] }>();
    const childParentIds = new Set<number>();
    const standalone: Court[] = [];

    // First pass: identify all parent courts (courts that have children)
    courts.forEach((court) => {
      if (court.parent_court_id) {
        childParentIds.add(court.parent_court_id);
      }
    });

    // Second pass: organize courts
    courts.forEach((court) => {
      if (court.parent_court_id) {
        // This is a child court - add to parent's children array
        if (!parentMap.has(court.parent_court_id)) {
          parentMap.set(court.parent_court_id, { parent: null as any, children: [] });
        }
        parentMap.get(court.parent_court_id)!.children.push(court);
      } else if (childParentIds.has(court.id)) {
        // This is a parent court (has children)
        if (!parentMap.has(court.id)) {
          parentMap.set(court.id, { parent: court, children: [] });
        } else {
          parentMap.get(court.id)!.parent = court;
        }
      } else {
        // This is a standalone court (no parent, no children)
        standalone.push(court);
      }
    });

    // Filter out any groups where parent wasn't found (edge case)
    const grouped = Array.from(parentMap.values()).filter((g) => g.parent !== null);

    return { grouped, standalone };
  }, [courts]);

  // Order schedule items so courts sharing the same physical space are adjacent
  type ScheduleItem =
    | { type: 'grouped'; data: { parent: Court; children: Court[] } }
    | { type: 'standalone'; court: Court };

  const orderedScheduleItems = useMemo((): ScheduleItem[] => {
    // Build physical space groups: key = physical_court_id or unique fallback
    const physicalGroups = new Map<string, ScheduleItem[]>();

    const getKey = (physicalCourtId?: string | null, fallbackId?: number) =>
      physicalCourtId || `_no_physical_${fallbackId}`;

    // Add grouped courts (parent+children)
    courtGroups.grouped.forEach((group) => {
      const key = getKey(group.parent.physical_court_id, group.parent.id);
      if (!physicalGroups.has(key)) physicalGroups.set(key, []);
      physicalGroups.get(key)!.push({ type: 'grouped', data: group });
    });

    // Add standalone courts
    courtGroups.standalone.forEach((court) => {
      const key = getKey(court.physical_court_id, court.id);
      if (!physicalGroups.has(key)) physicalGroups.set(key, []);
      physicalGroups.get(key)!.push({ type: 'standalone', court });
    });

    // Flatten: shared physical spaces (real physical_court_id) first, then individual courts
    const shared: ScheduleItem[] = [];
    const individual: ScheduleItem[] = [];

    physicalGroups.forEach((items, key) => {
      if (key.startsWith('_no_physical_')) {
        individual.push(...items);
      } else {
        shared.push(...items);
      }
    });

    return [...shared, ...individual];
  }, [courtGroups]);

  // ============================================================================
  // HANDLERS - FORM
  // ============================================================================

  const handleOpenAddForm = () => {
    if (!canManageCourts) return;
    setEditingCourt(null);
    setShowCourtForm(true);
  };

  const handleOpenEditForm = (court: Court) => {
    if (!canManageCourts) return;
    setEditingCourt(court);
    setShowCourtForm(true);
  };

  const handleCloseForm = () => {
    setShowCourtForm(false);
    setEditingCourt(null);
  };

  const handleFormSubmit = async (formData: CourtFormData, courtId?: number) => {
    try {
      if (courtId) {
        // Edit mode
        const result = await updateCourt.mutateAsync({
          courtId,
          formData,
        });
        showToast(result.message, 'success');
      } else {
        // Add mode
        const selectedSport = sports.find((s) => s.id === Number(formData.selectedSport));
        if (!selectedSport) {
          showToast('Please select a sport', 'error');
          return;
        }

        const courtNumber = getNextCourtNumber(courts, selectedSport.id);

        const courtDataWithName = {
          ...formData,
          name: formData.courtName.trim(),
          court_number: courtNumber,
        };

        const result = await addCourt.mutateAsync(courtDataWithName as any);
        showToast(result.message, 'success');
      }

      handleCloseForm();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save court';
      showToast(errorMessage, 'error');
      console.error('Failed to save court:', error);
    }
  };

  // ============================================================================
  // HANDLERS - TOGGLE ACTIVE
  // ============================================================================

  const handleToggleActive = async (court: Court, newActiveState: boolean) => {
    if (!canManageCourts) return;
    setTogglingCourtId(court.id);

    try {
      const result = await toggleActive.mutateAsync({
        courtId: court.id,
        isActive: newActiveState,
      });
      showToast(result.message, 'success');
    } catch (error) {
      if (error instanceof DeactivationBlockedError) {
        setDeactivationBlockedData({
          courtName: court.name,
          message: error.message,
          blockingReason: error.blockingReason,
          details: error.details,
        });
      } else {
        const errorMessage = error instanceof Error ? error.message : 'Failed to update court';
        showToast(errorMessage, 'error');
        console.error('Failed to toggle court active status:', error);
      }
    } finally {
      setTogglingCourtId(null);
    }
  };

  const handleCloseDeactivationModal = () => {
    setDeactivationBlockedData(null);
  };

  // ============================================================================
  // LOADING STATE
  // ============================================================================

  if (isPending) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading courts...</p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // ERROR STATE
  // ============================================================================

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Courts</h2>
        <p className="text-gray-600 mb-4">
          {error instanceof Error ? error.message : 'An error occurred'}
        </p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
        >
          Retry
        </button>
      </div>
    );
  }

  // ============================================================================
  // NO VENUE STATE
  // ============================================================================

  if (!venue) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <AlertCircle className="w-12 h-12 text-gray-400 mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">No Venue Found</h2>
        <p className="text-gray-600">Please ensure you have a venue associated with your account.</p>
      </div>
    );
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="p-4 sm:p-6 space-y-4">
      {/* Header */}
      <CourtsHeader onAddCourt={handleOpenAddForm} canManage={canManageCourts} />

      {/* Stats Cards */}
      <CourtsStats {...stats} />

      {/* Today's Court Schedule */}
      {courts.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-900">Today's Field/Court Schedule</h3>
            {sports.length > 1 && (
              <select
                value={filterSport}
                onChange={(e) => setFilterSport(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-semibold bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Sports</option>
                {sports.map((sport) => (
                  <option key={sport.id} value={sport.id}>
                    {sport.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="space-y-4">
            {/* Render courts ordered by shared physical space */}
            {orderedScheduleItems
              .filter((item) => {
                if (filterSport === 'all') return true;
                if (item.type === 'grouped') return item.data.parent.sport_id === filterSport;
                return item.court.sport_id === filterSport;
              })
              .map((item) => {
                if (item.type === 'grouped') {
                  return (
                    <GroupedTimeSlotCalendar
                      key={`group-${item.data.parent.id}`}
                      parentCourt={item.data.parent}
                      childCourts={item.data.children}
                      scheduleData={scheduleData ?? {}}
                      isLoading={scheduleLoading}
                    />
                  );
                }
                return (
                  <TimeSlotCalendar
                    key={item.court.id}
                    courtId={item.court.id}
                    courtName={item.court.name}
                    venueId={venue.id}
                    viewOnly={true}
                    scheduleData={scheduleData?.[item.court.id] ?? null}
                    isLoading={scheduleLoading}
                  />
                );
              })}
          </div>
        </div>
      )}

      {/* Courts Table */}
      <CourtsTable
        courts={courts}
        sports={sports}
        filterSport={filterSport}
        onFilterChange={setFilterSport}
        onEdit={handleOpenEditForm}
        onToggleActive={handleToggleActive}
        togglingCourtId={togglingCourtId}
        canManage={canManageCourts}
      />

      {/* Modals */}
      <CourtFormModal
        isOpen={showCourtForm}
        court={editingCourt}
        sports={sports}
        existingCourts={courts}
        onClose={handleCloseForm}
        onSubmit={handleFormSubmit}
        isSaving={addCourt.isPending || updateCourt.isPending}
      />

      <DeactivationBlockedModal
        isOpen={!!deactivationBlockedData}
        data={deactivationBlockedData}
        onClose={handleCloseDeactivationModal}
      />

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
